"use client";
import React, { useState } from "react";

import clsx from "clsx";
import { X } from "lucide-react";

import DomPurify from "isomorphic-dompurify";
import dynamic from "next/dynamic";
import Image from "next/image";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerTitle
} from "components/ui/drawer";
import { FadeInStagger } from "components/ui/FadeIn";
import { ScrollArea } from "components/ui/scroll-area";

import "./space.css";

import { Card, CardContent, CardHeader } from "components/ui/card";

const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));

type ModalContent = {
  src: string;
  title: string;
  content: string;
  mediaType: "image";
};

type BlockProps = {
  title: string;
  subtitle: string;
  mediaType: "image" | "video" | "icon";
  src: string;
  videoId?: string;
  meta?: string;
  blockBgColor: string;
  iconUrl: string;
  linkUrl?: string;
  linkCta?: string;
  openDrawer?: boolean;
  modalContent?: ModalContent[];
};

type BoxCardsProps = {
  blocks: BlockProps[];
  groupTitle?: string;
  sectionWrapperClassName?: string;
  groupSubtitle?: string;
  bgColor?: string;
  titleTag?: React.ElementType;
  miniTitle?: string;
  miniTitleTag?: React.ElementType;
  layout?: "grid" | "full";
  useGifThumbnail?: boolean;
};

const BalancedHTMLContent: React.FC<{ htmlContent: string }> = ({ htmlContent }) => {
  return <span dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(htmlContent) }} />;
};

export default function BoxCards({
  blocks,
  groupTitle,
  miniTitle,
  sectionWrapperClassName,
  titleTag = "h2",
  miniTitleTag = "h3",
  groupSubtitle,
  bgColor = "#ffffff",
  layout = "full",
  useGifThumbnail
}: BoxCardsProps) {
  const TitleTag = titleTag;
  const MiniTitleTag = miniTitleTag;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeBlock, setActiveBlock] = useState<BlockProps | null>(null);

  // Set default blockBgColor to #ffffff if null
  const processedBlocks = blocks.map((block) => ({
    ...block,
    blockBgColor: block.blockBgColor || "#ffffff"
  }));

  const handleBlockClick = (block: BlockProps) => {
    if (block.linkUrl) {
      // add http protocol
      let urlToRedirect = block.linkUrl;
      if (urlToRedirect.startsWith("www")) {
        urlToRedirect = "https://" + block.linkUrl;
      }
      window.location.href = urlToRedirect;
    } else {
      setActiveBlock(block);
      setIsDrawerOpen(true);
    }
  };

  return (
    <section
      id="box-cards-119"
      className={clsx("@container", sectionWrapperClassName)}
      style={{ backgroundColor: bgColor }}
    >
      <MaxWidthWrapper className="py-12">
        {groupTitle && (
          <div className="tile-title-container">
            {miniTitle && <MiniTitleTag className="subby text-center">{miniTitle}</MiniTitleTag>}
            {groupTitle && (
              <TitleTag className="tile-main-header font-heading [text-wrap:balance]">
                <BalancedHTMLContent htmlContent={groupTitle} />
              </TitleTag>
            )}
            {groupSubtitle && <p className="text-lg">{groupSubtitle}</p>}
          </div>
        )}
        <FadeInStagger faster>
          <section
            className={clsx(
              "mx-auto grid max-w-[90%]  gap-1",
              layout === "grid" && "md:grid-cols-2 md:gap-2.5 lg:grid-cols-3 lg:gap-5",
              layout === "full" && "grid-cols-1 gap-8 lg:grid-cols-2"
            )}
          >
            {processedBlocks.map((block, index) => {
              return (
                <div
                  key={index}
                  className={clsx(
                    "atom-link hoverable-space  icon-styles grid transform cursor-pointer place-items-center rounded-2xl border px-4 py-4 shadow-sm transition-transform duration-300",
                    layout === "full" && "p-2 md:grid-cols-1 md:gap-8 md:p-4"
                  )}
                  style={{ backgroundColor: block.blockBgColor }}
                  onClick={() => handleBlockClick(block)}
                >
                  <Card
                    className={clsx(
                      "clean bg-transparent",
                      layout === "full" && "w-full flex-grow md:flex md:flex-row"
                    )}
                  >
                    <CardHeader
                      className={clsx("mb-4", layout === "full" && "flex-grow md:mb-0 md:w-1/2 md:flex-shrink-0")}
                    >
                      <div
                        className={clsx(
                          "relative min-h-[150px]  w-full overflow-hidden rounded-2xl",
                          layout === "full" &&
                            "min-h-[150px] md:aspect-auto md:h-full md:min-h-[250px] lg:min-h-[300px]"
                        )}
                      >
                        {block.mediaType === "image" && (
                          <Image
                            src={block.src}
                            alt={`${block.title} Icon`}
                            fill
                            className="rounded-2xl"
                            style={{
                              objectFit: "cover"
                            }}
                          />
                        )}
                        {block.mediaType === "video" && (
                          <MuxPlayer
                            streamType="on-demand"
                            playbackId={block.videoId}
                            loop
                            muted
                            autoPlay
                            useGifThumbnail={useGifThumbnail}
                            className="border-primary-[#E8ECED] z-10 h-full w-full overflow-hidden rounded-xl rounded-b-none border object-cover"
                          />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent
                      className={clsx(layout === "full" && "flex flex-col justify-between md:w-1/2 md:pl-8")}
                    >
                      <div className="">
                        <span className="pb-1.5 pt-1 font-matter text-md font-medium leading-none"> {block.title}</span>
                        {block.subtitle && (
                          <p className={clsx("text-base text-primary-600", layout === "grid" && "line-clamp-2")}>
                            {block.subtitle}
                          </p>
                        )}
                      </div>
                      <div className="items-end align-bottom">
                        {block.meta && (
                          <p
                            className={clsx(
                              "text-base font-medium text-primary-700",
                              layout === "grid" && "line-clamp-2"
                            )}
                          >
                            {block.meta}
                          </p>
                        )}
                        <span className="link-with-chevron mt-4 flex items-center text-brandlight">Learn More</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </section>
        </FadeInStagger>
      </MaxWidthWrapper>
      {activeBlock && (
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerOverlay />
          <DrawerContent className="fixed bottom-0 left-0 right-0 mx-auto flex max-w-[1400px] flex-col overflow-hidden !rounded-t-[28px] bg-white">
            <ScrollArea className="h-full w-full">
              <DrawerClose className="absolute right-8 top-8 z-50">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#333336]">
                  <X className="text-white" />
                </span>
              </DrawerClose>

              <div className="relative  aspect-video w-full overflow-hidden rounded-t-[28px]">
                <DrawerTitle className="relative top-24 z-50 mx-auto max-w-[75%] !text-[58px] text-white">
                  {activeBlock.title}
                </DrawerTitle>
                {activeBlock.mediaType === "image" && (
                  <Image
                    src={activeBlock.src}
                    alt={`${activeBlock.title} Icon`}
                    fill
                    style={{
                      objectFit: "cover"
                    }}
                  />
                )}
                {activeBlock.mediaType === "video" && (
                  <MuxPlayer
                    streamType="on-demand"
                    playbackId={activeBlock.videoId}
                    loop
                    muted
                    autoPlay
                    useGifThumbnail={useGifThumbnail}
                    className="border-primary-[#E8ECED] z-10 h-full w-full overflow-hidden rounded-b-none rounded-t-[28px] border object-cover"
                  />
                )}
              </div>
              <div className="mx-auto my-28 max-w-[75%]">
                <DrawerDescription className="mb-28 !text-3xlt font-medium">
                  {activeBlock.subtitle || ""}
                </DrawerDescription>

                {activeBlock.modalContent &&
                  activeBlock.modalContent.map((contentItem, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-12 py-8 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}
                    >
                      {contentItem.src && (
                        <div className="w-1/2 flex-none">
                          <Image
                            src={contentItem.src}
                            alt={contentItem.title}
                            layout="responsive"
                            width={300}
                            height={300}
                            style={{
                              objectFit: "cover",
                              borderRadius: "28px"
                            }}
                          />
                        </div>
                      )}
                      <div className="flex flex-grow flex-col justify-center">
                        <h3 className="pb-4 text-2xl font-semibold">{contentItem.title}</h3>
                        <p className="text-lg text-[#717171]">{contentItem.content}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          </DrawerContent>
        </Drawer>
      )}
    </section>
  );
}
